import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ListDashes, MagnifyingGlass, DownloadSimple, Eye, ArrowClockwise } from "@phosphor-icons/react";
import DateRangePicker, { todayRange, rangeToParams } from "@/components/DateRangePicker";

const PAGE_SIZE = 25;
const EMPTY_FILTERS = { q: "", customer: "", ai_engine: "", campaign: "", transfer_result: "", disposition: "", amd_result: "" };

const buildFilterParams = (f, range) => {
  const p = {};
  Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
  return { ...p, ...rangeToParams(range) };
};
const nn = (v) => (v === null || v === undefined || v === "" ? "—" : v);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const dur = (s) => (s === null || s === undefined ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
const humanize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Disposition badge — green for a completed transfer, red for failure/blocked
// reasons (incl. calls that never reached the bot), neutral otherwise.
const DISP_FAIL = new Set([
  "no_response", "transfer_failed", "caller_hangup", "rejected_prefix",
  "no_runnable_flow", "engine_error", "no_audio", "failed", "error",
]);
// Friendly disposition labels. Raw engine reason codes get an operator-readable
// name; anything unmapped falls back to a Title-Cased version of the code.
const DISP_LABELS = { dead_end: "Incomplete Flow (no next step)" };
const dispLabel = (d) => DISP_LABELS[String(d || "").toLowerCase()] || humanize(d);
const dispClass = (d) => {
  if (!d) return "bg-muted text-muted-foreground";
  const k = String(d).toLowerCase();
  if (k === "transfer_succeeded" || k === "completed") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (DISP_FAIL.has(k)) return "bg-rose-100 text-rose-700 border border-rose-200";
  return "bg-amber-100 text-amber-700 border border-amber-200";
};

// Answering Machine Detection badge. human=green, machine=rose, notsure=amber.
const amdClass = (v) => {
  const k = String(v || "").toLowerCase();
  if (k === "human") return "bg-emerald-100 text-emerald-700 border border-emerald-200";
  if (k === "machine") return "bg-rose-100 text-rose-700 border border-rose-200";
  if (k === "notsure") return "bg-amber-100 text-amber-700 border border-amber-200";
  return "bg-muted text-muted-foreground";
};
const amdLabel = (v, action) => {
  if (String(action || "").toLowerCase() === "voicemail_left") return "Voicemail Left";
  return { human: "Human", machine: "Voicemail", notsure: "Not sure",
    disabled: "Off", error: "Err" }[String(v || "").toLowerCase()] || null;
};

export default function CDR() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const rowDisp = (r) => ((isReseller || isAdmin) && r.reseller_disposition) ? r.reseller_disposition : r.disposition;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [range, setRange] = useState(todayRange());
  const [appliedRange, setAppliedRange] = useState(todayRange());
  const [engines, setEngines] = useState([]);
  const [details, setDetails] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [dispOptions, setDispOptions] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioState, setAudioState] = useState("idle"); // idle|loading|ready|none|error

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, ...buildFilterParams(applied, appliedRange) };
      const { data } = await api.get("/cdr", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [page, applied, appliedRange]);

  useEffect(() => { load(); }, [load]);
  // Fetch the AMD recording for the open Call Detail (blob so the auth header is sent).
  useEffect(() => {
    if (!details?.id) { setAudioUrl(null); setAudioState("idle"); return; }
    let cancelled = false; let objUrl = null;
    setAudioState("loading"); setAudioUrl(null);
    api.get(`/cdr/${details.id}/audio`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        objUrl = window.URL.createObjectURL(res.data);
        setAudioUrl(objUrl); setAudioState("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        setAudioState(e.response?.status === 404 ? "none" : "error");
      });
    return () => { cancelled = true; if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [details?.id]);
  useEffect(() => {
    api.get("/cdr/stats", { params: buildFilterParams(applied, appliedRange) })
      .then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, [applied, appliedRange]);
  useEffect(() => {
    api.get("/ai-engines/overview").then(({ data }) => setEngines(data.engines || [])).catch(() => {});
    api.get("/cdr/stats").then(({ data }) => setDispOptions(Object.keys(data.by_disposition || {}))).catch(() => {});
  }, []);

  // Non-admins can't read the engine registry — derive engine options from loaded rows.
  const engineOptions = engines.length ? engines.map((e) => ({ id: e.engine_id, name: e.name }))
    : [...new Map(rows.filter((r) => r.ai_engine_id)
        .map((r) => [r.ai_engine_id, { id: r.ai_engine_id, name: r.ai_engine_name || r.ai_engine_id }])).values()];

  const apply = () => { setPage(1); setApplied(filters); setAppliedRange(range); };
  const reset = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setRange(todayRange()); setAppliedRange(todayRange()); setPage(1); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams(applied, appliedRange);
      const res = await api.get("/cdr/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "cdrs.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <PageHeader title="Call Detail Records"
        subtitle="Per-call VICIdial metadata and outcome for AI-engine calls (Lead ID, phone, campaign, disposition).">
        <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="cdr-export-btn" className="rounded-lg">
          <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </PageHeader>

      {/* Disposition summary cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="cdr-stats-cards">
          <div className="rounded-xl border border-border bg-card card-shadow p-4" data-testid="cdr-stat-total">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total Calls</p>
            <p className="text-2xl font-semibold tabular-nums mt-1">{stats.total}</p>
          </div>
          {Object.entries(stats.by_disposition || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card card-shadow p-4" data-testid={`cdr-stat-disp-${k}`}>
              <p className="text-xs uppercase tracking-wide text-muted-foreground truncate" title={dispLabel(k)}>{dispLabel(k)}</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card card-shadow p-4 mb-5 grid gap-3 md:grid-cols-6" data-testid="cdr-filters">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Search (phone / lead / unique id)</Label>
          <Input data-testid="cdr-search" value={filters.q} placeholder="e.g. 1005551234"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">AI Engine</Label>
          <select data-testid="cdr-filter-engine" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.ai_engine} onChange={(e) => setFilters({ ...filters, ai_engine: e.target.value })}>
            <option value="">All</option>
            {engineOptions.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Campaign</Label>
          <Input data-testid="cdr-filter-campaign" value={filters.campaign} placeholder="id / prefix"
            onChange={(e) => setFilters({ ...filters, campaign: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Transfer result</Label>
          <select data-testid="cdr-filter-transfer" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.transfer_result} onChange={(e) => setFilters({ ...filters, transfer_result: e.target.value })}>
            <option value="">All</option>
            <option value="transfer_succeeded">Transfer succeeded</option>
            <option value="transfer_failed">Transfer failed</option>
            <option value="not_attempted">Not attempted</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Disposition</Label>
          <select data-testid="cdr-filter-disposition" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.disposition} onChange={(e) => setFilters({ ...filters, disposition: e.target.value })}>
            <option value="">All</option>
            {dispOptions.map((d) => <option key={d} value={d}>{dispLabel(d)}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">AMD</Label>
          <select data-testid="cdr-filter-amd" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.amd_result} onChange={(e) => setFilters({ ...filters, amd_result: e.target.value })}>
            <option value="">All</option>
            <option value="human">Human</option>
            <option value="machine">Voicemail</option>
            <option value="notsure">Not sure</option>
            <option value="disabled">Off</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Date range</Label>
          <DateRangePicker value={range} onChange={setRange} testid="cdr-daterange" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Customer</Label>
          <Input data-testid="cdr-filter-customer" value={filters.customer} placeholder="Customer name"
            onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        <div className="md:col-span-6 flex gap-2">
          <Button size="sm" className="rounded-lg" onClick={apply} data-testid="cdr-apply-btn">
            <MagnifyingGlass className="w-4 h-4 mr-1.5" /> Apply
          </Button>
          <Button size="sm" variant="ghost" className="rounded-lg" onClick={reset} data-testid="cdr-reset-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <span className="ml-auto self-center text-xs text-muted-foreground" data-testid="cdr-total">{total} records</span>
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="cdr-empty" icon={ListDashes} title="No call records"
            description="CDRs appear here after AI-engine calls complete. Each record captures the VICIdial handoff headers (Lead ID, Caller ID, CALLERID, Client ID, Campaign ID/prefix, Unique ID) and the call outcome." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="cdr-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Lead ID</TableHead><TableHead>Phone</TableHead><TableHead>CALLERID</TableHead>
                <TableHead>Client</TableHead><TableHead>Customer</TableHead><TableHead>Campaign</TableHead><TableHead>Unique ID</TableHead>
                <TableHead>Start</TableHead><TableHead className="text-right">Dur</TableHead>
                <TableHead>AI Engine</TableHead><TableHead>Transfer</TableHead><TableHead>Disposition</TableHead>
                <TableHead>AMD</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`cdr-row-${r.id}`}>
                    <TableCell className="font-mono text-xs" data-testid={`cdr-lead-${r.id}`}>{nn(r.lead_id)}</TableCell>
                    <TableCell className="font-mono text-sm">{nn(r.phone_number)}</TableCell>
                    <TableCell className="text-sm">{nn(r.callerid)}</TableCell>
                    <TableCell className="font-mono text-xs">{nn(r.client_id)}</TableCell>
                    <TableCell className="text-xs" data-testid={`cdr-customer-${r.id}`}>{nn(r.customer_name)}</TableCell>
                    <TableCell className="text-xs">{nn(r.campaign_id)}{r.campaign_prefix ? ` / ${r.campaign_prefix}` : ""}</TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-[130px]">{nn(r.unique_id)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmt(r.start_time)}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{dur(r.duration_sec)}</TableCell>
                    <TableCell className="text-xs">{nn(r.ai_engine_name || r.ai_engine_id)}</TableCell>
                    <TableCell className="text-xs">{nn(r.transfer_result)}</TableCell>
                    <TableCell className="text-xs">
                      {r.disposition
                        ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${dispClass(rowDisp(r))}`} data-testid={`cdr-disp-${r.id}`}>{dispLabel(rowDisp(r))}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {amdLabel(r.amd_result, r.amd_action)
                        ? <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${amdClass(r.amd_result)}`} data-testid={`cdr-amd-${r.id}`} title={r.amd_cause || ""}>{amdLabel(r.amd_result, r.amd_action)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => setDetails(r)} data-testid={`cdr-details-${r.id}`}><Eye className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="cdr-pagination">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="cdr-prev">Prev</Button>
            <Button variant="outline" size="sm" className="rounded-lg" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="cdr-next">Next</Button>
          </div>
        </div>
      )}

      {/* Details */}
      <Dialog open={!!details} onOpenChange={(v) => !v && setDetails(null)}>
        <DialogContent data-testid="cdr-details-dialog">
          <DialogHeader><DialogTitle>Call Detail</DialogTitle>
            <DialogDescription>Unique ID {nn(details?.unique_id)}</DialogDescription></DialogHeader>
          <div className="py-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {[
              ["Lead ID", details?.lead_id], ["Phone (Caller-Id)", details?.phone_number],
              ["CALLERID (value)", details?.callerid], ["Client ID", details?.client_id],
              ["Campaign ID", details?.campaign_id], ["Campaign prefix", details?.campaign_prefix],
              ["Unique ID", details?.unique_id], ["FS UUID", details?.fs_uuid],
              ["AI Engine", details?.ai_engine_name || details?.ai_engine_id],
              ["AI Engine IP", details?.ai_engine_ip],
              [isAdmin ? "OpenSIPS IP" : "Telephony IP", details?.opensips_ip],
              [isAdmin ? "FreeSWITCH IP" : "Media IP", details?.freeswitch_ip],
              ...(isAdmin ? [["Capacity at selection", details?.capacity_at_selection != null ? `${details.capacity_at_selection}${details.max_capacity != null ? ` / ${details.max_capacity}` : ""}` : null]] : []),
              ["Start", details?.start_time && fmt(details.start_time)],
              ["End", details?.end_time && fmt(details.end_time)],
              ["Duration", details?.duration_sec != null ? dur(details.duration_sec) : null],
              ["Transfer result", details?.transfer_result], ["Disposition", details?.disposition ? dispLabel(details.disposition) : null],
              ...((isAdmin || isReseller) && details?.reseller_disposition ? [["Reseller disposition", dispLabel(details.reseller_disposition)]] : []),
              ["Hangup cause", details?.hangup_cause],
              ["AMD result", amdLabel(details?.amd_result, details?.amd_action)],
              ["AMD cause", details?.amd_cause],
              ["AMD decided (ms)", details?.amd_decided_ms],
              ["Transcript", details?.transcript_reference],
            ].map(([k, v]) => (
              <div key={k} data-testid={`cdr-detail-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{k}</p>
                <p className="font-mono text-xs break-all">{nn(v)}</p>
              </div>
            ))}
          </div>
          {/* Call recording — the AMD listening-window sample captured on the engine */}
          <div className="mt-1 border-t border-border pt-3" data-testid="cdr-recording">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">Call Recording (AMD sample)</p>
            {audioState === "loading" && <p className="text-xs text-muted-foreground">Loading recording…</p>}
            {audioState === "ready" && audioUrl && (
              <audio controls preload="none" src={audioUrl} className="w-full" data-testid="cdr-audio-player" />
            )}
            {audioState === "none" && <p className="text-xs text-muted-foreground" data-testid="cdr-audio-none">No recording available for this call.</p>}
            {audioState === "error" && <p className="text-xs text-rose-600" data-testid="cdr-audio-error">Couldn't load recording (engine unreachable).</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
